"use client";

import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shadcn-components/ui/select";
import { Button } from "@/shadcn-components/ui/button";
import { Badge } from "@/shadcn-components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shadcn-components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shadcn-components/ui/command";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";
import { useChartStore } from "@/vybe-trader-client/chart/stores/useChartStore";
import { useChartStore2 } from "@/vybe-trader-client/chart/stores/useChartStore2";
import { useChartStore3 } from "@/vybe-trader-client/chart/stores/useChartStore3";
import { useChartPanelId } from "@/components/chart/ChartPanelContext";
import { LiveSymbolIcon } from "@/components/common";

interface KLineChartSymbolSelectorProps {
  className?: string;
  isDesktop?: boolean;
}

export default function KLineChartSymbolSelector({
  className = "",
  isDesktop = false,
}: KLineChartSymbolSelectorProps) {
  const [open, setOpen] = useState(false);
  const chartPanelId = useChartPanelId();

  // Get subscribed symbols from store
  const allSubscribed = useSubscribedSymbolsStore(
    (state) => state.allSubscribed
  );

  // Get selected symbol and setter from chart store
  const selectedSymbol = useChartStore3(
    (state) => state.slots[chartPanelId].selectedSymbol,
  );

  // Extract symbol names from subscribed data
  const availableSymbols = allSubscribed.map((entry) => ({
    value: entry.symbol.currency,
    label: entry.symbol.currency,
    symbol: entry.symbol,
  }));

  const handleSymbolSelect = (symbolCurrency: string) => {
    // Find the symbol object
    const symbolEntry = allSubscribed.find(
      (entry) => entry.symbol.currency === symbolCurrency
    );
    if (symbolEntry) {
      useChartStore3
        .getState()
        .setSelectedSymbol(symbolEntry.symbol, chartPanelId);
    }
    setOpen(false);
  };

  // Sort symbols alphabetically
  const sortedSymbols = availableSymbols.sort((a, b) =>
    a.value.localeCompare(b.value)
  );

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={`${
              isDesktop ? "h-5 min-w-[88px] text-[11px]" : "h-6 min-w-[88px] text-xs"
            } justify-between font-medium border-border px-2`}
          >
            {selectedSymbol?.currency ? (
              <Badge
                variant="outline"
                className={`${
                  isDesktop ? "text-[11px]" : "text-xs"
                } font-medium border-none bg-transparent p-0 gap-1.5`}
              >
                <LiveSymbolIcon
                  name={selectedSymbol.currency}
                  group={selectedSymbol.groupName}
                  description={selectedSymbol.description}
                  size={isDesktop ? 12 : 14}
                />
                {selectedSymbol.currency}
              </Badge>
            ) : (
              <span className="text-muted-foreground">Select Symbol</span>
            )}
            <ChevronDown
              className={`ml-2 ${
                isDesktop ? "h-2 w-2" : "h-2.5 w-2.5"
              } shrink-0 opacity-50`}
            />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" align="start">
          <Command>
            <CommandInput
              placeholder="Search symbols..."
              className={`${isDesktop ? "h-6 text-xs" : "h-8 text-sm"}`}
            />
            <CommandList>
              <CommandEmpty className="py-3 text-center text-sm text-muted-foreground">
                No symbols found.
              </CommandEmpty>
              <CommandGroup>
                {sortedSymbols.length === 0 ? (
                  <div className="py-3 text-center text-sm text-muted-foreground">
                    No subscribed symbols
                  </div>
                ) : (
                  sortedSymbols.map((symbolOption) => (
                    <CommandItem
                      key={symbolOption.value}
                      value={symbolOption.value}
                      onSelect={() => handleSymbolSelect(symbolOption.value)}
                      className="text-sm"
                    >
                      <Check
                        className={`mr-2 h-4 w-4 ${
                          selectedSymbol?.currency === symbolOption.value
                            ? "opacity-100"
                            : "opacity-0"
                        }`}
                      />
                      <LiveSymbolIcon
                        name={symbolOption.symbol.currency}
                        group={symbolOption.symbol.groupName}
                        description={symbolOption.symbol.description}
                        size={16}
                        className="mr-2"
                      />
                      <span className="font-medium">{symbolOption.label}</span>
                      {selectedSymbol?.currency === symbolOption.value && (
                        <Badge variant="default" className="ml-auto text-xs">
                          Active
                        </Badge>
                      )}
                    </CommandItem>
                  ))
                )}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
